import React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * PageHeader - Reusable header component, based on the SpacesSearchView header design.
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {number} props.totalCount - Optional total count to display next to title
 * @param {string} props.subtitle - Optional subtitle
 * @param {Array<{label: string, path?: string}>} props.breadcrumbs - Optional breadcrumbs array
 * @param {ReactNode} props.rightContent - Optional content to display on the right (e.g., action buttons, sort dropdown)
 * @param {ReactNode} props.bottomContent - Optional content to display at the bottom (e.g., search bar, active filters)
 */
const PageHeader = ({ title, totalCount, subtitle, breadcrumbs, rightContent, bottomContent }) => {
  return (
    <header className="bg-card flex-shrink-0">
      <div className="max-w-[1600px] mx-auto px-6 py-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Left: Title */}
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {title} {totalCount !== undefined && <span className="text-muted-foreground">({totalCount})</span>}
              </h1>
              {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
            </div>
          </div>

          {/* Right: Custom Content */}
          {rightContent && (
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
              {rightContent}
            </div>
          )}
        </div>

        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="mt-3" aria-label="Breadcrumb">
            <div className="flex items-center flex-wrap gap-2 text-sm">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
                  {crumb.path ? (
                    <Link to={crumb.path} className="text-gray-600 hover:text-gray-900 transition-colors">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-gray-900 font-medium">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </nav>
        )}

        {/* Bottom Content (e.g., search bar, filter chips) */}
        {bottomContent}
      </div>
    </header>
  );
};

export default PageHeader;
